import { HTMLAttributes, ReactNode, useState } from 'react'
import { Copy, Trash2 } from 'lucide-react'
import { TFunction } from 'i18next'
import { useSortable } from '@dnd-kit/sortable'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { v4 as uuid } from 'uuid'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
} from 'app/frontend/types/custom/RaidPlan'
import Difficulty, {
  difficultiesByGameVersion,
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import { MultiSelect } from '@/components/ui/multi-select'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import TeamPlanAssignmentScopeCard from './TeamPlanAssignmentScopeCard'
import TeamPlanAssignmentVisualCard from './TeamPlanAssignmentVisualCard'
import { RaidstratsPlayer } from './RaidstratsContainer'
import useAbilityVisualPlanData from '../hooks/useAbilityVisualPlanData'
import AbilityVisualPopover from './AbilityVisualPopover'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentCastCount,
  getEffectiveExpectedEntities,
  normalizeAbilityDifficulties,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentType,
  getAssignmentScopes,
  getSoakPrefix,
  isGeneralAssignmentType,
  resolveAssignmentViewMode,
  shouldShowAssignmentViewModeTabs,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import { getDuplicateIdentitiesByScopeId } from './assignmentDuplicateIdentities'
import { FocusedAssignmentScope, getAbilityRowDragId } from './assignmentDnd'
import { flattenGroupsWithAnyPlayerFillers } from './assignmentEntityCounting'
import { getAssignmentUsageCountByIdentity } from './assignmentUsageCounts'
import SpellIcon from './SpellIcon'
import AssignmentTypeIcon from './AssignmentTypeIcon'
import TeamPlanAssignmentDialog from './TeamPlanAssignmentDialog'
import {
  buildAssignmentPhaseTimestampBadgeData,
  PhaseDivider,
} from './AssignmentPhaseTimestampBadge'
import { cn } from '@/lib/utils'
import { EncounterAbilityRow } from '../hooks/useTeamPlanComputedData'
import { CUSTOM_ABILITY_SPELL_ID } from '../utils/encounterAssignmentRows'
import AssignmentDescriptionContent, {
  AssignmentDescriptionControl,
} from './AssignmentDescriptionField'
import {
  AssignmentDependencyData,
  getAvailableDependencyOptions,
  getEffectiveDependency,
  getValidDependencyOption,
  isSameDependency,
} from './assignmentDependencies'
import AssignmentDependencyLine from './AssignmentDependencyLine'

type Scope = {
  id: string
  label: string
  castIndexes: number[]
  castGroups: number[][]
}

type PaletteEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
  label: ReactNode
  category:
    | 'collection'
    | 'group'
    | 'specific_character'
    | 'by_role'
    | 'by_class'
}

type Props = {
  row: EncounterAbilityRow
  t: TFunction
  canEdit: boolean
  encounterId: number | undefined
  difficulty: Difficulty
  owner: RaidPlanOwner
  basePath: string
  openRaidPlanAbilityKey: string | null
  setOpenRaidPlanAbilityKey: (abilityKey: string | null) => void
  setConfirmRemoveAbilityKey: (abilityKey: string | null) => void
  setAssignmentDialogOpen: (open: boolean) => void
  focusedScope: FocusedAssignmentScope | null
  onFocusScope: (scope: FocusedAssignmentScope | null) => void
  popupScope: { abilityKey: string; scopeId: string } | null
  onPopupScopeChange: (
    scope: { abilityKey: string; scopeId: string } | null,
  ) => void
  showSidebar: boolean
  paletteEntities: PaletteEntity[]
  paletteLabelByIdentity: Map<string, ReactNode>
  memberInfoByCharacterId: Record<number, { name: string; className: string }>
  specializationById: Record<
    number,
    {
      name: string
      icon: string
      classId: number
      className: string
    }
  >
  storedAbility?: RaidPlanEncounterAssignmentAbility
  updateAbility: (
    abilityKey: RaidPlanEncounterAssignmentAbilityKey,
    spellId: number,
    castCount: number,
    assignmentGroups: number,
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory?: boolean,
  ) => void
  onCommit: () => void
  removeAbility: (abilityKey: RaidPlanEncounterAssignmentAbilityKey) => void
  duplicateAbility?: (abilityKey: RaidPlanEncounterAssignmentAbilityKey) => void
  phaseDividers: PhaseDivider[]
  dependencyData: AssignmentDependencyData
}

const TeamPlanAssignmentRow = ({
  row,
  t,
  canEdit,
  encounterId,
  difficulty,
  owner,
  basePath,
  openRaidPlanAbilityKey,
  setOpenRaidPlanAbilityKey,
  setConfirmRemoveAbilityKey,
  setAssignmentDialogOpen,
  focusedScope,
  onFocusScope,
  popupScope,
  onPopupScopeChange,
  showSidebar,
  paletteEntities,
  paletteLabelByIdentity,
  memberInfoByCharacterId,
  specializationById,
  storedAbility,
  updateAbility,
  onCommit,
  removeAbility,
  duplicateAbility,
  phaseDividers,
  dependencyData,
}: Props) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: getAbilityRowDragId(row.abilityKey),
    disabled: !canEdit,
  })
  const { showAssignmentDifficulty, gameVersion = 'live' } =
    useTeamPlanTimeline()
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const isCustomAbility = row.isCustom
  const isGeneralAssignment = isGeneralAssignmentType(
    getEffectiveAssignmentType(storedAbility?.assignment_type),
  )
  const normalizedAssignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    storedAbility?.assignment_groups,
  )
  const effectiveCastCount = getEffectiveAssignmentCastCount(
    row.entries.length,
    storedAbility?.assignment_type,
  )
  const abilityAssignment: RaidPlanEncounterAssignmentAbility = {
    spell_id: row.spellId,
    casts: Array.from({ length: effectiveCastCount }, (_, castIndex) =>
      normalizeAssignmentCast(
        storedAbility?.casts?.[castIndex],
        normalizedAssignmentGroups,
      ),
    ),
    assignment_groups: storedAbility?.assignment_groups,
    expected_entities: storedAbility?.expected_entities,
    cascade_reminders_by: storedAbility?.cascade_reminders_by,
    offset_reminders_by: storedAbility?.offset_reminders_by,
    reminder_duration: storedAbility?.reminder_duration,
    depends_on: storedAbility?.depends_on,
    assignment_type: storedAbility?.assignment_type,
    title: storedAbility?.title,
    description: storedAbility?.description,
    message: storedAbility?.message,
    tts_message: storedAbility?.tts_message,
    cast_overrides: storedAbility?.cast_overrides,
    rgb: storedAbility?.rgb,
    raid_plan_link: storedAbility?.raid_plan_link,
    visual_id: storedAbility?.visual_id,
    visual_screen_ids: storedAbility?.visual_screen_ids,
    view_mode: storedAbility?.view_mode,
    nsrt_interrupt_module: storedAbility?.nsrt_interrupt_module,
    difficulty: storedAbility?.difficulty,
    custom_grouping: getEffectiveAbilityCustomGrouping(
      storedAbility?.spell_id ?? row.spellId,
      storedAbility?.custom_grouping,
    ),
  }
  const titleColor = abilityAssignment.rgb ?? row.color
  const availableDependencyOptions = getAvailableDependencyOptions(
    dependencyData,
    row.abilityKey,
  )
  const abilityDependencyOption = getValidDependencyOption(
    dependencyData,
    row.abilityKey,
    abilityAssignment.depends_on,
  )
  const effectiveAssignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    abilityAssignment.assignment_groups,
  )
  const abilityViewMode: AssignmentViewMode = resolveAssignmentViewMode(
    abilityAssignment.view_mode,
    effectiveCastCount,
    abilityAssignment.assignment_type,
    abilityAssignment.nsrt_interrupt_module,
  )
  const showViewModeTabs = shouldShowAssignmentViewModeTabs(
    effectiveCastCount,
    abilityAssignment.assignment_type,
    abilityAssignment.nsrt_interrupt_module,
  )
  const abilityCustomGrouping = getEffectiveAbilityCustomGrouping(
    abilityAssignment.spell_id,
    abilityAssignment.custom_grouping,
  )
  const scopes: Scope[] = getAssignmentScopes(
    row.entries,
    abilityViewMode,
    t,
    abilityCustomGrouping,
    abilityAssignment.assignment_type,
    row.isCustom,
  )
  const duplicateIdentitiesByScopeId = getDuplicateIdentitiesByScopeId({
    ability: abilityAssignment,
    scopes,
    assignmentGroups: effectiveAssignmentGroups,
    viewMode: abilityViewMode,
  })
  const assignmentUsageCountByIdentity = getAssignmentUsageCountByIdentity({
    ability: abilityAssignment,
    scopes,
    assignmentGroups: effectiveAssignmentGroups,
  })
  const hasAssignedEntities = abilityAssignment.casts.some((cast) =>
    normalizeAssignmentCast(cast, effectiveAssignmentGroups).boxes.some(
      (box) => normalizeAssignmentBox(box).entities.length > 0,
    ),
  )
  const iconInteractionProps = canEdit
    ? ({ ...attributes, ...listeners } as HTMLAttributes<HTMLSpanElement>)
    : undefined
  const noteSettingsLabel = abilityAssignment.title ?? row.name
  const noteSettingsPlaceholderText =
    abilityAssignment.message?.trim() ||
    `${getSoakPrefix(
      noteSettingsLabel,
      abilityAssignment.assignment_type,
      row.isCustom,
    )}${noteSettingsLabel}`
  const noteSettingsPlaceholder = {
    message: noteSettingsPlaceholderText,
    ttsMessage: noteSettingsPlaceholderText,
  }
  const getEntityLabel = (entity: RaidPlanAssignmentEntity) =>
    paletteLabelByIdentity.get(getAssignmentEntityIdentity(entity)) ??
    getAssignmentEntityLabel(t, entity)
  const scopeComputations = scopes.map((scope) => {
    const representativeCast = normalizeAssignmentCast(
      abilityAssignment.casts[scope.castIndexes[0]],
      effectiveAssignmentGroups,
    )
    const scopeBoxes = Array.from(
      { length: effectiveAssignmentGroups },
      (_, boxIndex) =>
        normalizeAssignmentBox(representativeCast.boxes[boxIndex]),
    )
    const scopeEntitiesByGroup = scopeBoxes.map((box) => box.entities)
    const noteSettingsByGroup = scopeBoxes.map((box) => ({
      message: box.message,
      ttsMessage: box.tts_message,
      expectedEntities: box.expected_entities,
      rgb: box.rgb,
      cascadeReminders: box.cascade_reminders_by,
      offsetReminders: box.offset_reminders_by,
      reminderDuration: box.reminder_duration,
      dependsOn: box.depends_on,
    }))
    const scopeTime = row.entries[scope.castIndexes[0]]?.time ?? 0
    const groupDependencyOptions = scopeBoxes.map((box) =>
      isSameDependency(box.depends_on, abilityAssignment.depends_on)
        ? undefined
        : getValidDependencyOption(
            dependencyData,
            row.abilityKey,
            box.depends_on,
          ),
    )
    const lockedDependencyCharacterIdsByGroup = scopeBoxes.map((box) => {
      const dependency = getEffectiveDependency(
        abilityAssignment.depends_on,
        box.depends_on,
        row.abilityKey,
        dependencyData.groupsByAbilityKey,
      )
      if (!dependency) return null
      return dependencyData.getLockedCharacterIds(dependency, scopeTime)
    })
    const flatScopeEntities = flattenGroupsWithAnyPlayerFillers(
      scopeEntitiesByGroup,
      (boxIndex) =>
        getEffectiveExpectedEntities(
          abilityAssignment.expected_entities,
          scopeBoxes[boxIndex]?.expected_entities,
        ),
    )
    return {
      scope,
      duplicateIdentities:
        duplicateIdentitiesByScopeId[scope.id] ?? new Set<string>(),
      scopeEntitiesByGroup,
      noteSettingsByGroup,
      flatScopeEntities,
      groupDependencyOptions,
      lockedDependencyCharacterIdsByGroup,
    }
  })
  const entitiesByScopeId = scopeComputations.reduce<
    Record<string, RaidPlanAssignmentEntity[]>
  >((acc, { scope, flatScopeEntities }) => {
    acc[scope.id] = flatScopeEntities
    return acc
  }, {})
  const raidPlanPlayers = scopeComputations
    .flatMap(({ flatScopeEntities }) => flatScopeEntities)
    .flatMap<RaidstratsPlayer>((entity) => {
      if (entity.kind !== 'member') {
        return []
      }
      const memberInfo = memberInfoByCharacterId[entity.character_id]
      const specialization = entity.spec_id
        ? specializationById[entity.spec_id]
        : undefined
      const className = specialization?.className ?? memberInfo?.className
      const name = memberInfo?.name ?? entity.label
      if (!className || !name) {
        return []
      }
      return [{ name, className }]
    })
  const dedupedRaidPlanPlayers = Array.from(
    new Map(
      raidPlanPlayers.map((player) => [
        `${player.name}|${player.className}`,
        player,
      ]),
    ).values(),
  )
  const { visualPlan, visualRaidPlan } = useAbilityVisualPlanData({
    owner,
    ability: abilityAssignment,
    scopes,
    entitiesByScopeId,
    entries: row.entries,
    phaseDividers,
    specializationById,
    memberInfoByCharacterId,
    raidPlanPlayers: dedupedRaidPlanPlayers,
    getEntityLabel,
  })

  return (
    <div
      ref={setNodeRef}
      className='flex flex-col gap-2 mb-8'
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        opacity: isDragging ? 0 : 1,
      }}
    >
      {showAssignmentDifficulty ? (
        <div className='flex items-center justify-between gap-2'>
          <div className='min-w-[180px] max-w-xs'>
            <MultiSelect<Difficulty>
              value={normalizeAbilityDifficulties(abilityAssignment.difficulty)}
              onChange={(values) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  effectiveCastCount,
                  normalizedAssignmentGroups,
                  (ability) => {
                    ability.difficulty = values.length > 0 ? values : undefined
                  },
                  true,
                )
              }
              options={(
                difficultiesByGameVersion[gameVersion] ??
                difficultiesByGameVersion.live
              ).map((value) => ({
                value,
                label: difficultiesLabels[value],
              }))}
              placeholder='Any'
              selectedDisplay='count'
            />
          </div>
          {canEdit && duplicateAbility ? (
            <Button
              type='button'
              variant='outline'
              title={t('dashboard.team.plans.assignments.duplicate')}
              onClick={() => duplicateAbility(row.abilityKey)}
            >
              <Copy className='h-4 w-4' />
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className='flex items-center justify-between gap-2 flex-wrap'>
        <div className='flex items-center gap-2 min-w-0'>
          {isCustomAbility ? (
            <div className='flex items-center gap-2 text-xl min-w-0'>
              <span
                ref={setActivatorNodeRef}
                {...iconInteractionProps}
                className={cn(
                  'inline-flex',
                  canEdit ? 'cursor-grab active:cursor-grabbing' : undefined,
                )}
              >
                <SpellIcon
                  spellId={CUSTOM_ABILITY_SPELL_ID}
                  className='w-8 h-8 rounded border border-border'
                />
              </span>
              <div className='flex min-w-0 flex-col'>
                <div className='flex min-w-0 items-center gap-2 leading-tight'>
                  <AssignmentTypeIcon
                    assignmentType={abilityAssignment.assignment_type}
                  />
                  <span className='truncate' style={{ color: titleColor }}>
                    {abilityAssignment.title ?? row.name}
                  </span>
                </div>
                <AssignmentDependencyLine option={abilityDependencyOption} />
              </div>
            </div>
          ) : (
            <div className='flex items-center gap-2 text-xl min-w-0'>
              <span
                ref={setActivatorNodeRef}
                {...iconInteractionProps}
                className={cn(
                  'inline-flex',
                  canEdit ? 'cursor-grab active:cursor-grabbing' : undefined,
                )}
              >
                <SpellIcon
                  spellId={row.spellId}
                  className='w-8 h-8 rounded border border-border'
                />
              </span>
              <div className='flex min-w-0 flex-col'>
                <div className='flex min-w-0 items-center gap-2 leading-tight'>
                  <AssignmentTypeIcon
                    assignmentType={abilityAssignment.assignment_type}
                  />
                  <a
                    href={`https://wowhead.com/spell=${row.spellId}`}
                    target='_blank'
                    rel='noreferrer'
                    className='truncate min-w-0'
                    style={{ color: titleColor }}
                  >
                    {abilityAssignment.title ?? row.name}
                  </a>
                </div>
                <AssignmentDependencyLine option={abilityDependencyOption} />
              </div>
            </div>
          )}
        </div>
        {canEdit ? (
          <div className='flex items-center gap-2 flex-wrap justify-end'>
            <AssignmentDescriptionControl
              description={abilityAssignment.description}
              canEdit={canEdit}
              isEditing={isEditingDescription}
              onStartEdit={() => setIsEditingDescription(true)}
              className='h-10'
            />
            {showViewModeTabs ? (
              <Tabs
                value={abilityViewMode}
                onValueChange={(value) =>
                  updateAbility(
                    row.abilityKey,
                    row.spellId,
                    effectiveCastCount,
                    effectiveAssignmentGroups,
                    (ability) => {
                      ability.view_mode = value as AssignmentViewMode
                    },
                    true,
                  )
                }
              >
                <TabsList>
                  <TabsTrigger value='all_together'>
                    {t('dashboard.team.plans.assignments.view-modes.all')}
                  </TabsTrigger>
                  <TabsTrigger value='alternating'>
                    {t(
                      'dashboard.team.plans.assignments.view-modes.alternating',
                    )}
                  </TabsTrigger>
                  <TabsTrigger value='all_separate'>
                    {t('dashboard.team.plans.assignments.view-modes.separate')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
            <TeamPlanAssignmentDialog
              open={openRaidPlanAbilityKey === row.abilityKey}
              onOpenChange={(open) =>
                setOpenRaidPlanAbilityKey(open ? row.abilityKey : null)
              }
              row={row}
              ability={abilityAssignment}
              spellName={abilityAssignment.title ?? row.name}
              defaultTitle={
                isCustomAbility
                  ? t('dashboard.team.plans.assignments.custom')
                  : row.name
              }
              canEdit={canEdit}
              assignmentGroups={effectiveAssignmentGroups}
              dependencyOptions={availableDependencyOptions}
              allDependencyOptions={dependencyData.options}
              onUpdate={(updater, pushHistory) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  effectiveCastCount,
                  effectiveAssignmentGroups,
                  updater,
                  pushHistory,
                )
              }
              onCommit={onCommit}
            />
            <AbilityVisualPopover
              ability={abilityAssignment}
              encounterId={encounterId}
              difficulty={difficulty}
              owner={owner}
              basePath={basePath}
              canEdit={canEdit}
              onUpdate={(updater, pushHistory) =>
                updateAbility(
                  row.abilityKey,
                  row.spellId,
                  effectiveCastCount,
                  effectiveAssignmentGroups,
                  updater,
                  pushHistory,
                )
              }
              onCommit={onCommit}
              t={t}
            />
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                if (!hasAssignedEntities) {
                  removeAbility(row.abilityKey)
                  return
                }
                setConfirmRemoveAbilityKey(row.abilityKey)
              }}
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </div>
        ) : null}
      </div>

      <AssignmentDescriptionContent
        description={abilityAssignment.description}
        isEditing={isEditingDescription}
        clearTarget='this assignment'
        onStopEdit={() => setIsEditingDescription(false)}
        onSave={async (html) => {
          updateAbility(
            row.abilityKey,
            row.spellId,
            effectiveCastCount,
            effectiveAssignmentGroups,
            (ability) => {
              ability.description = html || undefined
            },
          )
          setIsEditingDescription(false)
        }}
      />

      {scopes.length === 0 && !isGeneralAssignment ? (
        <div className='flex items-center gap-1 text-sm text-muted-foreground'>
          <span>This ability has no casts.</span>
          {canEdit ? (
            <>
              {' '}
              <span>Add casts in the </span>
              <Button
                type='button'
                variant='outline'
                className='px-1.5'
                size='xs'
                onClick={() => setOpenRaidPlanAbilityKey(row.abilityKey)}
              >
                settings
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {visualPlan || visualRaidPlan ? (
        <TeamPlanAssignmentVisualCard
          label={t('dashboard.team.plans.assignments.visual-section')}
          visualPlan={visualPlan}
          visual={visualRaidPlan}
        />
      ) : null}

      {scopeComputations.map(
        ({
          scope,
          duplicateIdentities,
          scopeEntitiesByGroup,
          noteSettingsByGroup,
          groupDependencyOptions,
          lockedDependencyCharacterIdsByGroup,
        }) => (
          <TeamPlanAssignmentScopeCard
            key={scope.id}
            scope={scope}
            spellId={row.spellId}
            castTimestamps={
              isGeneralAssignment
                ? []
                : scope.castGroups.map((castGroup) =>
                    buildAssignmentPhaseTimestampBadgeData({
                      castIndexes: castGroup,
                      entries: row.entries,
                      phaseDividers,
                    }),
                  )
            }
            entitiesByGroup={scopeEntitiesByGroup}
            assignmentGroups={effectiveAssignmentGroups}
            abilityExpectedEntities={abilityAssignment.expected_entities}
            canEdit={canEdit}
            paletteEntities={paletteEntities}
            assignmentUsageCountByIdentity={assignmentUsageCountByIdentity}
            onSave={(nextEntitiesByGroup) =>
              updateAbility(
                row.abilityKey,
                row.spellId,
                effectiveCastCount,
                effectiveAssignmentGroups,
                (ability) => {
                  scope.castIndexes.forEach((castIndex) => {
                    const cast = normalizeAssignmentCast(
                      ability.casts[castIndex],
                      effectiveAssignmentGroups,
                    )
                    nextEntitiesByGroup.forEach((nextEntities, boxIndex) => {
                      cast.boxes[boxIndex] = {
                        ...normalizeAssignmentBox(cast.boxes[boxIndex]),
                        entities: nextEntities.map((entity) => ({
                          ...entity,
                          id: entity.id ?? uuid(),
                        })),
                      }
                    })
                    ability.casts[castIndex] = cast
                  })
                },
                true,
              )
            }
            assignLabel={t('dashboard.team.plans.assignments.assign')}
            showViewModeTabs={showViewModeTabs}
            abilityViewMode={abilityViewMode}
            duplicateIdentities={duplicateIdentities}
            getEntityLabel={getEntityLabel}
            groupLabel={(count) =>
              t('dashboard.team.plans.assignments.group-label', { count })
            }
            onAssignmentDialogOpenChange={setAssignmentDialogOpen}
            abilityKey={row.abilityKey}
            focusedScope={focusedScope}
            onFocusScope={onFocusScope}
            popupOpen={
              popupScope?.abilityKey === row.abilityKey &&
              popupScope?.scopeId === scope.id
            }
            onPopupOpenChange={(open) =>
              onPopupScopeChange(
                open ? { abilityKey: row.abilityKey, scopeId: scope.id } : null,
              )
            }
            showSidebar={showSidebar}
            description={
              storedAbility?.casts?.[scope.castIndexes[0]]?.description
            }
            onDescriptionSave={async (html) => {
              updateAbility(
                row.abilityKey,
                row.spellId,
                effectiveCastCount,
                effectiveAssignmentGroups,
                (ability) => {
                  scope.castIndexes.forEach((castIndex) => {
                    const cast = normalizeAssignmentCast(
                      ability.casts[castIndex],
                      effectiveAssignmentGroups,
                    )
                    cast.description = html || undefined
                    ability.casts[castIndex] = cast
                  })
                },
              )
            }}
            noteSettingsByGroup={noteSettingsByGroup}
            dependencyOptions={availableDependencyOptions}
            allDependencyOptions={dependencyData.options}
            abilityDependsOn={abilityAssignment.depends_on}
            groupDependencyOptions={groupDependencyOptions}
            lockedDependencyCharacterIdsByGroup={
              lockedDependencyCharacterIdsByGroup
            }
            noteSettingsPlaceholder={noteSettingsPlaceholder}
            rgbPlaceholder={abilityAssignment.rgb ?? row.color}
            abilityMessage={abilityAssignment.message}
            abilityLabel={noteSettingsLabel}
            abilityCascadeReminders={abilityAssignment.cascade_reminders_by}
            abilityOffsetReminders={abilityAssignment.offset_reminders_by}
            abilityReminderDuration={abilityAssignment.reminder_duration}
            assignmentType={abilityAssignment.assignment_type}
            isCustom={row.isCustom}
            onEditForAllReminders={() =>
              setOpenRaidPlanAbilityKey(row.abilityKey)
            }
            onNoteSettingsSave={(boxIndex, next) =>
              updateAbility(
                row.abilityKey,
                row.spellId,
                effectiveCastCount,
                effectiveAssignmentGroups,
                (ability) => {
                  scope.castIndexes.forEach((castIndex) => {
                    const cast = normalizeAssignmentCast(
                      ability.casts[castIndex],
                      effectiveAssignmentGroups,
                    )
                    const box = normalizeAssignmentBox(cast.boxes[boxIndex])
                    box.message = next.message
                    box.tts_message = next.ttsMessage
                    box.expected_entities = next.expectedEntities
                    box.rgb = next.rgb
                    box.cascade_reminders_by = next.cascadeReminders
                    box.offset_reminders_by = next.offsetReminders
                    box.reminder_duration = next.reminderDuration
                    box.depends_on = next.dependsOn
                    cast.boxes[boxIndex] = box
                    ability.casts[castIndex] = cast
                  })
                },
                true,
              )
            }
          />
        ),
      )}
    </div>
  )
}

export default TeamPlanAssignmentRow
